import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { NativeProps, CSpacing, CSeverity } from '@/src/components/interfaces';
import { CBottomPanel } from '@/src/components/c-bottom-panel/CBottomPanel';
import { usePresenterStore } from '@/src/infrastructure/presenter/store';
import { CButton, CButtonPattern, CButtonShape } from '@/src/components/c-button/CButton';
import { CFlex, JustifyContent } from '@/src/components/c-flex/CFlex';
import { CIcons } from '@/src/components/c-icon/CIcon';
import { CText, CTextSize, CTextColor } from '@/src/components/c-text/CText';
import { CFieldLabel, CFieldSecurity } from '@/src/components/c-field-label/CFieldLabel';
import { CFieldValidation, FieldValidation } from '@/src/components/c-field-validations/CFieldValidation';
import { useCTranslations, useCPortal, CPortalLayer } from '@/src/infrastructure/provider';
import { CWheel, CWheelItem } from '@/src/components/c-wheel/CWheel';

export interface CTimePickerTranslations {
  cancel?: string;
  confirm?: string;
  now?: string;
  title?: string;
}

export interface CTimeValue {
  hour: number;
  minute: number;
}

export enum CTimePickerMinuteStep {
  One = 1,
  Five = 5,
  Ten = 10,
  Fifteen = 15,
  Twenty = 20,
  Thirty = 30,
}

export interface CTimePickerProps extends NativeProps {
  value?: CTimeValue;
  onChange: (time: CTimeValue) => void;
  minuteStep?: CTimePickerMinuteStep;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  security?: CFieldSecurity;
  validations?: FieldValidation[];
  hideMessageContainer?: boolean;
  translations?: CTimePickerTranslations;
}

const HOUR_ITEMS: CWheelItem[] = Array.from({ length: 24 }, (_, i) => ({ value: i, label: String(i).padStart(2, '0') }));

function getMinuteItems(step: number): CWheelItem[] {
  const count = Math.floor(60 / step);
  return Array.from({ length: count }, (_, i) => {
    const value = i * step;
    return { value, label: String(value).padStart(2, '0') };
  });
}

const isMobile = () => window.innerWidth <= 480;

export function CTimePicker(props: CTimePickerProps): React.JSX.Element {
  const translations = useCTranslations();
  const now = new Date();
  const minuteStep = props.minuteStep ?? CTimePickerMinuteStep.One;
  const initial = props.value ?? { hour: now.getHours(), minute: now.getMinutes() };
  const [selectedHour, setSelectedHour] = useState(initial.hour);
  const [selectedMinute, setSelectedMinute] = useState(initial.minute);
  const [open, setOpen] = useState(false);
  const presenter = usePresenterStore();
  const portalContainer = useCPortal(CPortalLayer.Popover);
  const triggerRef = useRef<HTMLDivElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  const minuteItems = useMemo(() => getMinuteItems(minuteStep), [minuteStep]);

  useEffect(() => {
    if (props.value) {
      setSelectedHour(props.value.hour);
      setSelectedMinute(props.value.minute);
    }
  }, [props.value]);

  const handleConfirm = useCallback(() => {
    props.onChange({ hour: selectedHour, minute: selectedMinute });
    setOpen(false);
    presenter.reject();
  }, [selectedHour, selectedMinute, props.onChange, presenter]);

  const handleCancel = useCallback(() => {
    const time = props.value ?? { hour: now.getHours(), minute: now.getMinutes() };
    setSelectedHour(time.hour);
    setSelectedMinute(time.minute);
    setOpen(false);
    presenter.reject();
  }, [props.value, presenter]);

  const handleNow = useCallback(() => {
    const current = new Date();
    setSelectedHour(current.getHours());
    setSelectedMinute(Math.floor(current.getMinutes() / minuteStep) * minuteStep);
  }, [minuteStep]);

  const handleOpen = useCallback(() => {
    if (props.disabled) return;
    if (isMobile()) {
      void presenter.show(undefined as any);
    } else {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setDropdownStyle({
          top: rect.bottom + 4,
          left: rect.left,
          width: Math.max(rect.width, 220),
        });
      }
      setOpen(true);
    }
  }, [props.disabled, presenter]);

  const displayValue = props.value
    ? `${String(props.value.hour).padStart(2, '0')}:${String(props.value.minute).padStart(2, '0')}`
    : '';

  const status = useMemo(() => {
    const hasErrors = props.validations?.some(v => v.severity === CSeverity.Error) ?? false;
    const hasWarnings = props.validations?.some(v => v.severity === CSeverity.Warning) ?? false;
    if (hasErrors) return 'error';
    if (hasWarnings) return 'warning';
    return 'neutral';
  }, [props.validations]);

  const wheelsContent = (
    <div className="c-time-picker__wheels-container">
      <div className="c-time-picker__wheels">
        <CWheel items={HOUR_ITEMS} selected={selectedHour} onChange={setSelectedHour} />
        <div className="c-time-picker__separator">:</div>
        <CWheel items={minuteItems} selected={selectedMinute} onChange={setSelectedMinute} />
      </div>
      <CFlex justifyContent={JustifyContent.Center} class="c-time-picker__now">
        <CButton
          text={props.translations?.now ?? translations.timePicker.now}
          pattern={CButtonPattern.Secondary}
          shape={CButtonShape.Square}
          onClick={handleNow}
        />
      </CFlex>
    </div>
  );

  return (
    <div
      id={props.id}
      ref={triggerRef}
      className={classNames('c-time-picker', {
        'c-time-picker--disabled': props.disabled,
        'c-time-picker--full-width': props.fullWidth,
        [`c-time-picker--status-${status}`]: status !== 'neutral',
      }, props.class)}
      data-test-selector={props.testSelector ?? 'c-time-picker'}
      style={props.style}
    >
      {props.label && (
        <CFieldLabel label={props.label} required={props.required} security={props.security} />
      )}

      <button
        type="button"
        className={classNames('c-time-picker__trigger', { 'c-time-picker__trigger--open': open })}
        onClick={handleOpen}
        disabled={props.disabled}
      >
        <CText size={CTextSize.Md} color={displayValue ? undefined : CTextColor.Gray5}>
          {displayValue || props.placeholder || ''}
        </CText>
      </button>

      {props.validations && props.validations.length > 0 && !props.hideMessageContainer && (
        <CFieldValidation validations={props.validations} />
      )}

      {open && createPortal(
        <>
          <div className="c-time-picker__overlay" onClick={handleCancel} />
          <div className="c-time-picker__dropdown" style={dropdownStyle}>
            {wheelsContent}
            <div className="c-time-picker__actions">
              <CButton text={props.translations?.cancel ?? translations.timePicker.cancel} pattern={CButtonPattern.Secondary} shape={CButtonShape.Square} onClick={handleCancel} />
              <CButton text={props.translations?.confirm ?? translations.timePicker.confirm} pattern={CButtonPattern.Primary} shape={CButtonShape.Square} onClick={handleConfirm} />
            </div>
          </div>
        </>,
        portalContainer ?? document.body
      )}

      {createPortal(
        <CBottomPanel
          scrollable={false}
          presenter={presenter}
          header={{ title: props.translations?.title ?? translations.timePicker.title ?? props.label ?? 'Select time' }}
          footer={{
            slot: (
              <CFlex gap={CSpacing.Md} class="c-time-picker__footer-buttons">
                <CButton icon={CIcons.X} text={props.translations?.cancel ?? translations.timePicker.cancel} shape={CButtonShape.Square} pattern={CButtonPattern.Secondary} onClick={handleCancel} class="c-time-picker__footer-btn" />
                <CButton icon={CIcons.Check} text={props.translations?.confirm ?? translations.timePicker.confirm} shape={CButtonShape.Square} pattern={CButtonPattern.Primary} onClick={handleConfirm} class="c-time-picker__footer-btn" />
              </CFlex>
            ),
          }}
        >
          {wheelsContent}
        </CBottomPanel>,
        portalContainer ?? document.body
      )}
    </div>
  );
}
